// BLOG-007: AI delivery team structure post
import { ArrowRight } from "lucide-react";
import Nav from "../../components/Nav";
import Footer from "../../components/Footer";
import PostHero from "../../components/PostHero";
import Reveal from "../../components/Reveal";

export default function AiDeliveryTeamStructure() {
  return (
    <div className="bg-black">
      <Nav route="inner" />

      <PostHero
        category="Engineering"
        title="Senior squads, not body shops: how we structure AI delivery teams"
        author="Engineering"
        date="February 2026"
        readTime="4 min read"
        excerpt="The staffing model most AI consultancies run — large teams of junior
         engineers under a distant technical lead — is the wrong model for
         industrial AI. Here is the structure that ships, and what to ask
         before you sign."
      />

      <main>
        <div className="max-w-3xl mx-auto px-6 py-12">

          <Reveal>
            <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight mt-2">
              The body shop model and why it fails
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              The typical large-consultancy AI engagement puts a senior architect on the
              account for 2 days a week and fills the rest of the team with junior engineers
              who are learning on your project. The senior person does the design. The juniors
              do the implementation. You pay senior rates for junior delivery.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              In consumer software that model can limp through — the environment is familiar,
              the tooling is well-documented and the failure modes are recoverable. In
              industrial environments — mining, machinery, defence, geospatial — the operating
              constraints are unfamiliar, the integration complexity is high and a failed
              deployment costs real money and real trust. Junior engineers learning those
              environments on a live project create delivery risk that compounds week by week.
              For AI transformation work, where the system has to survive contact with
              operations, the body shop fails quietly and expensively.
            </p>
          </Reveal>

          <div className="hairline my-12" />

          <Reveal>
            <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight mt-2">
              What a senior squad looks like
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              Our delivery model is 1 to 3 senior engineers embedded full-time for the
              duration of the engagement — the structure behind our{" "}
              <a href="/services/engineering-squads.html" className="text-white/90 underline decoration-white/30 underline-offset-4 hover:decoration-white/70 transition-colors">Engineering as a Service</a>
              {" "}offering. Senior means 10 or more years of production experience, not 10
              years in AI specifically. An engineer who has shipped complex software in
              demanding environments and then gone deep on AI tooling for 2 years is worth
              more to your project than an AI researcher who has never run production software.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              The squad is small enough to move fast and broad enough to cover the critical
              disciplines: data engineering, model development and AI integration. Where the
              work needs deep domain knowledge — mining operations, SCADA systems, geospatial
              data pipelines — we embed alongside your domain experts rather than hiring
              generalists onto our side. That is how a prototype becomes production AI without
              a 20-person programme: small senior teams, your operators in the loop, working
              software every week.
            </p>
          </Reveal>

          <div className="hairline my-12" />

          <Reveal>
            <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight mt-2">
              The knowledge transfer problem
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              The most common complaint about AI consultancy engagements is that the knowledge
              leaves with the consultants. The system is delivered, the team departs and nobody
              inside the client organisation can maintain or extend it. You bought a system and
              inherited a dependency.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              We treat knowledge transfer as a delivery requirement, not an optional extra. The
              engineer embedded with your team writes the documentation as they build. They
              pair with your technical staff through the critical implementation phases. The
              handover is structured and evidenced: running the system together in production,
              not handing over a document and a zip file. At the end you own the code, the
              pipeline and the operating knowledge.
            </p>
          </Reveal>

          <div className="hairline my-12" />

          <Reveal>
            <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight mt-2">
              Sizing the engagement correctly
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              The right engagement size depends on scope, not ambition. A 2-engineer squad
              over 12 weeks can take a predictive maintenance system from prototype to
              production on a mid-sized fleet. Adding a third engineer does not make it 3
              times faster — it adds coordination overhead and often slows delivery in the
              first half of the engagement.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              We push back on scope inflation. When requirements arrive mid-engagement, we
              assess whether they fit the current squad and timeline before committing — the
              alternative is a project that expands until it cannot ship anything at all. And
              if the scope itself is still unclear — you know AI matters to the business but
              not where to start — that is a scoping problem, not a staffing problem. It is
              better solved by a short{" "}
              <a href="/services/ai-consultancy.html" className="text-white/90 underline decoration-white/30 underline-offset-4 hover:decoration-white/70 transition-colors">AI transformation consultancy</a>
              {" "}engagement before a squad is booked.
            </p>
          </Reveal>

          <div className="hairline my-12" />

          <Reveal>
            <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight mt-2">
              What to ask an AI consultancy before you sign
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              Ask to speak with the engineers who will be on your project, not the partners
              who will sell you the engagement. Ask how many of those engineers have shipped
              AI in production in your sector. Ask what the handover process looks like and
              what documentation you will own at the end.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              Then ask what happens when something breaks in production 6 months after the
              engagement ends. The right answer is specific: who picks up the phone, on what
              terms, at what cost. The test is whether the consultancy ships and then stands
              behind the system — transparent, written support terms with named engineers are
              a sign of confidence, not a sales hook. The warning sign is vagueness in either
              direction: a partner who disappears at handover, or one whose commercial model
              depends on you never becoming self-sufficient.
            </p>
          </Reveal>

        </div>
      </main>

      <section className="bg-black px-6 pb-24 mt-4">
        <div className="max-w-2xl mx-auto liquid-glass rounded-3xl p-10 text-center">
          <h3 className="font-instrument text-3xl text-white">
            Put a senior squad on it
          </h3>
          <p className="text-white/60 text-base mt-4">
            Engineering as a Service embeds 1 to 3 senior engineers with your team — from
            Perth or Auckland, on site or remote — to ship production AI and hand over a
            system you own, with transparent support after delivery.
          </p>
          <a
            href="/#contact"
            className="mt-8 inline-flex items-center gap-2 rounded-full px-8 py-3
                       bg-white text-black text-sm font-medium hover:bg-white/90
                       transition-colors"
          >
            Talk to an engineer <ArrowRight size={16} />
          </a>
          <div>
            <a href="/services/engineering-squads.html" className="mt-4 inline-flex items-center gap-2 liquid-glass rounded-full px-8 py-3 text-white text-sm font-medium hover:bg-white/5 transition-colors">Explore Engineering Squads <ArrowRight size={16} /></a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
